<script lang="ts">
	/**
	 * EkaviMark — heart with negative-space 4-point AI sparkle cutout.
	 *
	 * Geometry matches `ekavi-growth/brand/mark.svg` v1.0 exactly:
	 *   · viewBox 100×100, two-cubic plump heart construction
	 *   · 4-point sparkle ("spark") at (68, 34), 21u radius, Gemini-style
	 *     16-segment cubic Bezier with tangent reversal at each tip —
	 *     producing rounded teardrop points instead of sharp ones.
	 *     Rendered in negative space against the heart so the "care"
	 *     reading dominates.
	 *
	 * Symbol meaning:
	 *   Heart   — care, the human outcome
	 *   Spark   — intelligence, the work being done by an AI
	 *   Together — "AI that works with care" (heart first, AI second)
	 *
	 * The mark is filled in `currentColor` so consumers can recolour
	 * with `style="color: var(--ekavi-charcoal)"`. Brand requires
	 * charcoal on bone — never gradient, never two-colour.
	 *
	 * Accessibility: when used decoratively (inside the lockup), pass
	 * `decorative={true}` so screen readers ignore it. Otherwise the
	 * default `Ekavi Care` label survives.
	 */

	interface Props {
		size?: number | string;
		title?: string;
		decorative?: boolean;
		/** Override the unique mask id when multiple marks share a page. */
		maskIdSuffix?: string;
	}

	let { size = 96, title = 'Ekavi Care', decorative = false, maskIdSuffix = '' }: Props = $props();

	const maskId = $derived(`ekavi-mark-spark${maskIdSuffix ? `-${maskIdSuffix}` : ''}`);
</script>

<svg
	xmlns="http://www.w3.org/2000/svg"
	width={size}
	height={size}
	viewBox="0 0 100 100"
	role={decorative ? 'presentation' : 'img'}
	aria-hidden={decorative ? 'true' : undefined}
	aria-label={decorative ? undefined : title}
	class="ekavi-mark"
>
	{#if !decorative}
		<title>{title}</title>
	{/if}
	<defs>
		<mask id={maskId}>
			<rect x="0" y="0" width="100" height="100" fill="white" />
			<path
				d="M 68 55 C 68 52.09, 67.44 49.36, 66.32 46.81 C 65.24 44.26, 63.75 42.03, 61.86 40.14 C 59.97 38.25, 57.75 36.76, 55.19 35.68 C 52.63 34.56, 49.90 34, 47 34 C 49.90 34, 52.63 33.46, 55.19 32.37 C 57.75 31.25, 59.97 29.75, 61.86 27.86 C 63.75 25.97, 65.24 23.74, 66.32 21.19 C 67.44 18.64, 68 15.91, 68 13 C 68 15.91, 68.54 18.64, 69.63 21.19 C 70.75 23.74, 72.25 25.97, 74.14 27.86 C 76.03 29.75, 78.25 31.24, 80.81 32.32 C 83.37 33.46, 86.10 34, 89 34 C 86.10 34, 83.37 34.56, 80.81 35.68 C 78.25 36.76, 76.03 38.25, 74.14 40.14 C 72.25 42.03, 70.75 44.26, 69.63 46.81 C 68.54 49.36, 68 52.09, 68 55 Z"
				fill="black"
			/>
		</mask>
	</defs>
	<path
		d="M 50 12 C 78 -18, 145 28, 50 92 C -45 28, 22 -18, 50 12 Z"
		fill="currentColor"
		mask="url(#{maskId})"
	/>
</svg>

<style>
	.ekavi-mark {
		display: inline-block;
		line-height: 0;
		flex-shrink: 0;
		color: var(--ekavi-charcoal);
	}
</style>
